import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDateTime } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useAssociateBailAccountMovementToBatch,
  useDisassociateBailAccountMovementFromBatch,
} from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { formatAgentName } from "../../../../agent/utils";
import { useHasPermissions } from "../../../../auth/hooks";
import { BailAccountMovement } from "../../../bailaccount/types";
import { CommissionsBatchStep, ManualBailAccountMovementsReport } from "../../enums";
import { ManualBailAccountMovementsFilterPageResult } from "../../types";

interface Props {
  movementsPage: ManualBailAccountMovementsFilterPageResult;
  loading?: boolean;
}

export const BatchManualBailAccountMovementsTable = ({ movementsPage, loading }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const hasManageCommissionsPermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const associateBailAccountMovementToBatch = useAssociateBailAccountMovementToBatch();
  const disassociateBailAccountMovementFromBatch = useDisassociateBailAccountMovementFromBatch();

  const columns = useMemo(() => {
    const { batch, report } = movementsPage;

    const items: ColumnsType<BailAccountMovement> = [
      {
        key: "agent",
        title: t("commissions.bailAccount.attrs.movements.agent"),
        width: 220,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{formatAgentName(record.agent)}</Ellipsis>,
      },
      {
        key: "createdAt",
        title: t("common.createdAt"),
        width: 160,
        render: (_, record) => formatLocaleDateTime(record.createdAt),
      },
      {
        key: "note",
        title: t("commissions.bailAccount.attrs.movements.note"),
        width: 300,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.note}</Ellipsis>,
      },
      {
        key: "amount",
        title: t("common.amount"),
        align: "right",
        width: 130,
        render: (_, record) => formatLocaleCurrency(record.amount),
      },
    ];

    if (batch.step === CommissionsBatchStep.RECAPITULATION && hasManageCommissionsPermission) {
      items.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 90,
        render: (_, record) =>
          report === ManualBailAccountMovementsReport.ASSOCIATED ? (
            <ActionTextIcon
              icon="branch"
              color="orange"
              text={t("common.exclude")}
              disabled={batch.stepChangeInProgress}
              onClick={() =>
                disassociateBailAccountMovementFromBatch.mutate({
                  id1: batch.id,
                  id2: record.id,
                  object: { optimisticLockVersion: record.optimisticLockVersion },
                })
              }
            />
          ) : (
            <ActionTextIcon
              icon="pull-request"
              color="green"
              text={t("common.include")}
              disabled={batch.stepChangeInProgress}
              onClick={() =>
                associateBailAccountMovementToBatch.mutate({
                  id1: batch.id,
                  id2: record.id,
                  object: { optimisticLockVersion: record.optimisticLockVersion },
                })
              }
            />
          ),
      });
    }

    return items;
  }, [movementsPage, hasManageCommissionsPermission]);

  return (
    <Table
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.MEDIUM }}
      dataSource={movementsPage.pageData}
      loading={loading}
      pagination={{
        ...paginationTableProps,
        placement: ["bottomEnd"],
        current: movementsPage.pageIndex + 1,
        pageSize: movementsPage.pageSize,
        total: movementsPage.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
